import { useState, useEffect, useCallback } from 'react';
import { Card, Button, Row, Col, ListGroup, Form, Image } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getInitialsFromName, resolveAvatarSrc } from 'utils/avatar';
import { getSocket } from '../../../../socket';

const getAvatarSrc = (avatar) => resolveAvatarSrc(avatar);

export default function Poll({ pollId, getPolls, attendeeCount = 0, isPollManager = false }) {
  const [poll, setPoll] = useState(null); // State to store poll data
  const [isLoading, setIsLoading] = useState(true); // State to track loading status
  const [error, setError] = useState(null); // State to track errors
  const [isBodyVisible, setIsBodyVisible] = useState(true); // State to toggle Card.Body visibility
  const [hasVoted, setHasVoted] = useState(false); // State to track if the user has voted
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [allVotedToastShown, setAllVotedToastShown] = useState(false);
  const [optionAvatars, setOptionAvatars] = useState({});
  const url = process.env.REACT_APP_URL; // Base URL from environment variables
  const { user: authUser } = useSelector((x) => x.auth);

  // Fetch poll data (stable via useCallback)
  const fetchPoll = useCallback(async () => {
    setIsLoading(true);
    setError(null); // Reset error state before fetching
    try {
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch poll: ${response.statusText}`);
      }

      const data = await response.json();
      setPoll(data); // Update poll state with fetched data
      if (authUser?.token) {
        setSelectedOptionId(data.myVoteOptionId || '');
        setHasVoted(data.status === 'active' && Boolean(data.myVoteOptionId));
      }
    } catch (error) {
      console.error(`An error occurred while fetching the poll: ${error.message}`);
      setError(error.message); // Set error message
    } finally {
      setIsLoading(false); // Stop loading spinner
    }
  }, [url, pollId, authUser?.token]);

  useEffect(() => {
    fetchPoll();
  }, [fetchPoll]);

  useEffect(() => {
    if (!poll?.meeting || !pollId) return undefined;

    const socket = getSocket();
    socket.emit('meeting:join', poll.meeting.toString());

    const handlePollStatusChanged = (payload) => {
      if (!payload || String(payload.pollId) !== String(pollId)) return;
      if (payload.status === 'deleted') {
        getPolls();
        return;
      }
      void fetchPoll();
    };

    socket.on('poll:status-changed', handlePollStatusChanged);

    return () => {
      socket.off('poll:status-changed', handlePollStatusChanged);
    };
  }, [fetchPoll, getPolls, poll?.meeting, pollId]);

  useEffect(() => {
    if (authUser?.token) return;
    const votedPolls = JSON.parse(localStorage.getItem('votedPolls') || '[]');
    const voted = Array.isArray(votedPolls) && votedPolls.some((entry) => (
      typeof entry === 'string'
        ? entry === pollId
        : entry?.id === pollId && Date.now() < Number(entry.expiry || 0)
    ));
    setHasVoted(voted);
  }, [authUser?.token, pollId]);

  useEffect(() => {
    let mounted = true;
    if (!poll || !Array.isArray(poll.options)) return () => { mounted = false };
    const baseUrl = url;

    poll.options.forEach((option) => {
      const key = String(option?._id || option?.tmAuthUserId || option?.userId || '').trim();
      if (!key) return;
      const existing = getAvatarSrc(option?.avatarUrl || option?.avatar);
      if (existing) return;
      if (optionAvatars[key]) return;

      (async () => {
        try {
          const resp = await fetch(`${baseUrl}/unified/${encodeURIComponent(key)}`);
          if (!mounted) return;
          if (!resp.ok) return;
          const payload = await resp.json().catch(() => null);
          const avatar = payload?.identity?.avatarUrl || payload?.identity?.avatar || null;
          const src = avatar ? getAvatarSrc(avatar) : null;
          if (src && mounted) setOptionAvatars(prev => ({ ...prev, [key]: src }));
        } catch (_err) {
          // ignore
        }
      })();
    });

    return () => { mounted = false };
    }, [poll, optionAvatars, url]);

  useEffect(() => {
    if (!poll || attendeeCount <= 0) {
      setAllVotedToastShown(false);
      return;
    }

    const totalVotes = (Array.isArray(poll.options) ? poll.options : [])
      .reduce((sum, option) => sum + (Number(option?.votes) || 0), 0);

    if (totalVotes === attendeeCount && !allVotedToastShown) {
      setAllVotedToastShown(true);
      return;
    }

    if (totalVotes !== attendeeCount && allVotedToastShown) {
      setAllVotedToastShown(false);
    }
  }, [poll, attendeeCount, allVotedToastShown]);

  const toggleBodyVisibility = () => {
    setIsBodyVisible((prev) => !prev); // Toggle the visibility of Card.Body
  };

  const handleVote = async () => {
    if (!selectedOptionId) {
      toast.error('Please select an option before voting.', { position: 'bottom-left' });
      return;
    }

    const optionId = selectedOptionId;

    try {
      // Fetch the latest poll data to verify the status
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch poll status: ${response.statusText}`);
      }

      const latestPoll = await response.json();

      // Check if the poll status is still 'active'
      if (latestPoll.status !== 'active') {
        toast.warning('The vote cannot be cast. Vote session is not active. Please reload the poll.', { position: 'bottom-left' });
        return;
      }

      // Proceed with submitting the vote
      const now = Date.now();
      const validPolls = authUser?.token
        ? []
        : JSON.parse(localStorage.getItem('votedPolls') || '[]').filter((entry) => (
          typeof entry === 'string' || now < Number(entry?.expiry || 0)
        ));
      if (!authUser?.token) localStorage.setItem('votedPolls', JSON.stringify(validPolls));

      // Check if the current poll is already voted
      if (authUser?.token || !validPolls.some((entry) => (typeof entry === 'string' ? entry : entry.id) === pollId)) {
        const voteResponse = await fetch(`${url}/vote/${pollId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
            // 'Authorization': `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({ optionId }),
        });

        if (!voteResponse.ok) {
          throw new Error(`Failed to submit vote: ${voteResponse.statusText}`);
        }

        if (!authUser?.token) {
          validPolls.push({ id: pollId, expiry: now + 10 * 60 * 1000 });
          localStorage.setItem('votedPolls', JSON.stringify(validPolls));
        }
        setHasVoted(true);

        await fetchPoll();
      } else {
        toast.info('You have already voted for this poll.', { position: 'bottom-left' });
      }
    } catch (error) {
      toast.error(`Failed to submit vote: ${error.message}`, { position: 'bottom-left' });
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authUser.token,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete poll: ${response.statusText}`);
      }

      window.dispatchEvent(new CustomEvent('meeting:poll-deleted', {
        detail: {
          meetingId: String(poll?.meeting || '').trim(),
          pollId: String(pollId || '').trim(),
          title: String(poll?.title || '').trim(),
        },
      }));

      getPolls();
    } catch (error) {
      toast.error(`Failed to delete poll: ${error.message}`, { position: 'bottom-left' });
    }
  };

  const handleStatusChange = async () => {
    if (!isPollManager || !poll) return;

    let nextStatus;
    switch (poll.status) {
      case 'ready':
        nextStatus = 'active';
        break;
      case 'active':
        nextStatus = 'closed';
        break;
      case 'closed':
        nextStatus = 'displayed';
        break;
      default:
        toast.error('Invalid poll status', { position: 'bottom-left' });
        return;
    }

    try {
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`,
        },
        body: JSON.stringify({ status: nextStatus }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update poll status: ${response.statusText}`);
      }

      await response.json();
      await fetchPoll();
      getPolls();
    } catch (error) {
      toast.error(`Failed to update poll status: ${error.message}`, { position: 'bottom-left' });
    }
  };

  // Determine if the user can view the poll
  const canViewPoll = () => {
    if (!poll || !authUser) return false;

    // Poll is visible to all signed-in users when status is 'active' or 'displayed'
    if (poll.status === 'active' || poll.status === 'displayed') {
      return true;
    }

    // Poll is visible to officers
    const isOfficer = authUser.acl === 'Officer';

    return isOfficer;
  };

  if (isLoading) {
    return <p>Loading poll...</p>;
  }

  if (error) {
    return <p className="text-danger">Error: {error}</p>;
  }

  if (!poll) {
    return <p>No poll data available.</p>;
  }

  if (!canViewPoll()) {
    // return <p>You do not have permission to view this poll.</p>;
  }

  return (
    <Card className="mt-3 mb-3 poll-static-card poll-admin-card">
      <Card.Header onClick={toggleBodyVisibility} style={{ cursor: 'pointer' }}>
        <Row className="poll-admin-header-row">
          <Col xs={10}>
            <span className="d-inline-flex align-items-center gap-2">
              {authUser?.user?.acl && authUser.user.acl === 'Officer' && (
                <Button
                  variant="danger"
                  size="sm"
                  className="p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    const confirmed = window.confirm('Are you sure you want to delete this poll?');
                    if (!confirmed) return;
                    handleDelete();
                  }}
                  aria-label="Delete poll"
                  title="Delete poll"
                >
                  <i className="bi bi-trash"></i>
                </Button>
              )}
              <span>{poll.title}</span>
            </span>
            {['ready', 'active', 'closed'].includes(poll.status) && isPollManager ? (
              poll.status === 'ready' ? (
                <span className="badge bg-success ms-2" style={{ cursor: 'pointer' }} onClick={handleStatusChange}>{poll.status}</span>
              ) : poll.status === 'active' ? (
                <span className="badge bg-warning ms-2" style={{ cursor: 'pointer' }} onClick={handleStatusChange}>{poll.status}</span>
              ) : (
                <span className="badge bg-danger ms-2" style={{ cursor: 'pointer' }} onClick={handleStatusChange}>{poll.status}</span>
              )
            ) : (
              poll.status === 'ready' ? (
                <span className="badge bg-success ms-2">{poll.status}</span>
              ) : poll.status === 'active' ? (
                <span className="badge bg-warning ms-2">{poll.status}</span>
              ) : poll.status === 'closed' ? (
                <span className="badge bg-danger ms-2">{poll.status}</span>
              ) : poll.status === 'displayed' ? (
                <span className="badge bg-info ms-2">{poll.status}</span>
              ) : null
            )}
          </Col>
          <Col xs={2} className="text-end">
            <div className="d-inline-flex align-items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                className="poll-admin-toggle-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  fetchPoll();
                  toast.info('Poll reloaded successfully', { position: 'bottom-left' });
                }}
                aria-label="Reload poll"
                title="Reload poll"
              >
                <i className="bi bi-arrow-clockwise"></i>
              </Button>
              <Button
                variant="success"
                size="sm"
                className="poll-admin-toggle-btn"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the Card.Header click event
                  toggleBodyVisibility();
                }}
                aria-label={isBodyVisible ? 'Collapse poll' : 'Expand poll'}
                title={isBodyVisible ? 'Collapse poll' : 'Expand poll'}
              >
                {isBodyVisible ? (
                  <i className="bi bi-chevron-up" variant="success"></i> // Arrow up icon
                ) : (
                  <i className="bi bi-chevron-down" variant="success"></i> // Arrow down icon
                )}
              </Button>
            </div>
          </Col>
        </Row>
      </Card.Header>
      {isBodyVisible && (
        <>
          <Card.Body>
            {hasVoted && poll.status === 'active' && !authUser?.token ? (
              <p className="text-center mt-3">
                Thank you for your vote! The results will be announced later.
              </p>
            ) : poll.status === 'active' ? (
              <>
                <ListGroup data-bs-theme={'dark'} className='poll-option-list'>
                  {poll.options.map((option, index) => (
                    <ListGroup.Item key={index} className='poll-option-card'>
                      <Row className="poll-option-row">
                        <Col xs={8} className='poll-option-main'>
                          {(() => {
                            const optionInitials = getInitialsFromName(option?.firstName, option?.lastName, 'T');
                            return (
                          <div className='poll-option-avatar'>
                            {(() => {
                              const key = String(option?._id || option?.tmAuthUserId || option?.userId || '').trim();
                              const src = getAvatarSrc(option?.avatarUrl || option?.avatar) || (key ? optionAvatars[key] : null);
                              return src ? <Image src={src} alt='Poll option avatar' /> : <span className='poll-option-avatar-fallback' aria-hidden='true'>{optionInitials}</span>;
                            })()}
                          </div>
                            );
                          })()}
                          <Form.Check
                            type="radio"
                            name={`poll-${pollId}`}
                            id={`poll-${pollId}-option-${option._id}`}
                            label={option.firstName + ' ' + option.lastName}
                            checked={selectedOptionId === String(option._id)}
                            onChange={() => setSelectedOptionId(String(option._id))}
                          />
                        </Col>
                        <Col>
                          {/* ({option.votes} votes) */}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
                <div className="text-center mt-3">
                  <Button variant="primary" onClick={handleVote}>
                    {hasVoted ? 'Update Vote' : 'Submit Vote'}
                  </Button>
                </div>
              </>
            ) : (
              <ListGroup data-bs-theme={'dark'} className='poll-option-list'>
                {poll.options.map((option, index) => {
                  const maxVotes = Math.max(...poll.options.map((opt) => opt.votes)); // Find the maximum votes
                  const isHighest = option.votes === maxVotes; // Check if this option has the highest votes

                  return (
                    <ListGroup.Item key={index} className='poll-option-card'>
                      <Row className="poll-option-row">
                        <Col className='poll-option-main'>
                          {(() => {
                            const optionInitials = getInitialsFromName(option?.firstName, option?.lastName, 'T');
                            return (
                          <div className='poll-option-avatar'>
                            {(() => {
                              const key = String(option?._id || option?.tmAuthUserId || option?.userId || '').trim();
                              const src = getAvatarSrc(option?.avatarUrl || option?.avatar) || (key ? optionAvatars[key] : null);
                              return src ? <Image src={src} alt='Poll option avatar' /> : <span className='poll-option-avatar-fallback' aria-hidden='true'>{optionInitials}</span>;
                            })()}
                          </div>
                            );
                          })()}
                          <span className="poll-option-name">{option.firstName} {option.lastName}</span>
                        </Col>
                        <Col xs={4} className="text-end">
                          {poll.status === 'displayed' && (
                            <span className={`badge ms-2 ${isHighest ? 'bg-warning' : 'bg-info'}`}>
                              {option.votes} {option.votes === 1 ? 'vote' : 'votes'}
                            </span>
                          )}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}

          </Card.Body>
        </>
      )}
    </Card>
  );
}
